import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPageCount,
  useBlogPosts,
  useBlogTools,
} from "@/tools/blog/hooks/use-blog-data";
import { useScrollTopOnChange } from "@/tools/blog/hooks/use-scroll-top-on-change";
import { BlogCover } from "../components/BlogCover";
import { BlogPagination } from "../components/BlogPagination";
import { BlogPostCard } from "../components/BlogPostCard";
import { BlogPostSkeleton } from "../components/BlogPostSkeleton";

export function BlogIndex({ page = 1 }: { page?: number }) {
  const locale = getLocale();
  useScrollTopOnChange(page);
  const { data, isLoading: postsLoading } = useBlogPosts(
    locale,
    undefined,
    undefined,
    page,
    true, // featured: page 1 fetches an extra post to keep the grid full
  );
  const { data: tools } = useBlogTools(locale);

  const posts = data?.items ?? [];
  const pageCount = blogPageCount(data?.total ?? 0, true);

  // The newest post is featured as a hero, but only on the first page.
  const showFeatured = page === 1 && posts.length > 0;
  const featuredPost = showFeatured ? posts[0] : undefined;
  const gridPosts = showFeatured ? posts.slice(1) : posts;

  const featuredDate = featuredPost?.publishedAt
    ? new Date(featuredPost.publishedAt).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="relative">
      {/* Atmospheric background */}
      <div className="pointer-events-none absolute inset-0 gradient-mesh" />
      <div className="pointer-events-none absolute inset-0 grid-pattern-fine opacity-30" />

      <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="animate-slide-up mb-12">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-secondary" />
            <span className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-secondary">
              {m.tool_blog_name()}
            </span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {m.tool_blog_description()}
          </h1>
        </div>

        {/* Tool filter pills */}
        {tools && tools.length > 0 && (
          <div className="animate-slide-up delay-100 mb-10 flex flex-wrap gap-2 opacity-0">
            <Link
              to="/blog"
              className="chip-on rounded-full px-5 py-2 text-sm font-semibold tracking-wide transition-all"
            >
              {m.blog_all_posts()}
            </Link>
            {tools.map((tool) => (
              <Link
                key={tool.id}
                to="/blog/$tool"
                params={{ tool: tool.slug }}
                className="chip-idle rounded-full px-5 py-2 text-sm font-semibold tracking-wide transition-all hover:border-primary/50 hover:bg-primary/10"
              >
                {tool.name}
              </Link>
            ))}
          </div>
        )}

        {/* Loading state */}
        {postsLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <BlogPostSkeleton key={i} />
            ))}
          </div>
        ) : posts.length > 0 ? (
          <>
            {/* Featured hero post (first page only) */}
            {featuredPost && (
              <Link
                to="/blog/$tool/$topic/$slug"
                params={{
                  tool: featuredPost.toolSlug,
                  topic: featuredPost.topicSlug,
                  slug: featuredPost.slug,
                }}
                className="animate-slide-up delay-200 group relative mb-14 block overflow-hidden rounded-2xl border border-border bg-card opacity-0 shadow-[0_1px_2px_oklch(0.2_0.03_265/0.06),0_8px_24px_-8px_oklch(0.2_0.03_265/0.12)] transition-all duration-500 hover:border-primary/60 hover:shadow-[0_2px_4px_oklch(0.2_0.03_265/0.08),0_16px_40px_-12px_oklch(0.2_0.03_265/0.2)] dark:border-white/[0.14] dark:bg-[oklch(0.255_0.022_265)] dark:shadow-[0_1px_0_oklch(1_0_0/0.1)_inset,0_30px_60px_-28px_rgba(0,0,0,0.95)] dark:ring-1 dark:ring-inset dark:ring-white/[0.04] dark:hover:shadow-[0_1px_0_oklch(1_0_0/0.14)_inset,0_30px_70px_-26px_oklch(0.5_0.22_260/0.45)]"
              >
                <div className="grid items-stretch lg:grid-cols-[1.25fr_1fr]">
                  <BlogCover
                    coverImage={featuredPost.coverImage}
                    coverImageMobile={featuredPost.coverImageMobile}
                    alt={featuredPost.title}
                    size="featured"
                    priority
                    className="aspect-[16/10] w-full lg:aspect-auto lg:min-h-[340px]"
                  />

                  {/* Content */}
                  <div className="relative flex flex-col justify-center p-7 sm:p-9 lg:p-10">
                    <span className="mb-3 inline-flex w-fit items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary">
                      <Sparkles className="h-3 w-3" />
                      {featuredPost.topicName}
                    </span>
                    <h2 className="mb-3 text-2xl font-bold leading-tight tracking-tight transition-colors group-hover:text-primary sm:text-3xl">
                      {featuredPost.title}
                    </h2>
                    <p className="mb-6 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {featuredPost.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5 font-mono text-[11px] text-muted-foreground/90">
                        {featuredDate && <time>{featuredDate}</time>}
                        <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/50" />
                        <span>
                          {featuredPost.readingTimeMinutes}{" "}
                          {m.blog_minutes_read()}
                        </span>
                      </div>
                      <span className="flex -translate-x-2 items-center gap-1 text-sm font-medium text-primary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100">
                        {m.blog_read_more()}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Posts grid */}
            {gridPosts.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {gridPosts.map((post, i) => (
                  <div
                    key={post.id}
                    className="animate-slide-up opacity-0"
                    style={{ animationDelay: `${300 + i * 100}ms` }}
                  >
                    <BlogPostCard post={post} />
                  </div>
                ))}
              </div>
            )}

            <BlogPagination page={page} pageCount={pageCount} to="/blog" />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-4 rounded-full bg-muted p-4">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-lg text-muted-foreground">{m.blog_no_posts()}</p>
          </div>
        )}
      </div>
    </div>
  );
}
